/**
 * React-Context/Provider der I18n_Infrastruktur.
 *
 * Der `I18nProvider` hält die Aktive_Sprache als State und stellt über
 * `useI18n()` die sprachgebundenen Helfer (`t`, `formatDate`, `formatTime`)
 * sowie `setLanguage` bereit. Er kennt ausschließlich Sprach_Cache und
 * Spracherkennung — es gibt hier bewusst KEINEN Backend-Aufruf (die
 * Persistenz-Verantwortung liegt im `AuthProvider`, Design-Komponente
 * "Provider-Reihenfolge").
 *
 * Folgt dem bestehenden Context-/Hook-Muster aus
 * `frontend/src/contexts/AuthContext.tsx` (inkl. klarer Developer-Fehlermeldung
 * bei Nutzung außerhalb des Providers).
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { catalog, type Language } from "./catalog";
import { translate, type TranslateParams } from "./translate";
import { readLanguage, writeLanguage } from "./languageStorage";
import { detectLanguage } from "./detect";
import {
  formatDate as baseFormatDate,
  formatTime as baseFormatTime,
} from "./datetime";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface I18nContextValue {
  /** Die Aktive_Sprache. */
  language: Language;
  /**
   * Setzt die Aktive_Sprache: aktualisiert den State UND schreibt den
   * Sprach_Cache (Req 4.2, 7.5). KEIN Backend-Aufruf — Persistenz liegt im
   * `AuthProvider`.
   */
  setLanguage: (lang: Language) => void;
  /** Löst einen Übersetzungs-Schlüssel in der Aktive_Sprache auf (Req 1.3). */
  t: (key: string, params?: TranslateParams) => string;
  /** Formatiert ein ISO-Datum in der Aktive_Sprache (Req 9.1). */
  formatDate: (iso: string, opts?: Intl.DateTimeFormatOptions) => string;
  /** Formatiert eine ISO-Zeitangabe in der Aktive_Sprache (Req 9.1). */
  formatTime: (iso: string, opts?: Intl.DateTimeFormatOptions) => string;
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const I18nContext = createContext<I18nContextValue | null>(null);

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

/**
 * Bestimmt die initiale Aktive_Sprache: zuerst der Sprach_Cache (Req 7.3), sonst
 * die aus der Browser-Spracheinstellung erkannte Sprache (Req 7.6, 8.1–8.3).
 */
function resolveInitialLanguage(): Language {
  return (
    readLanguage() ??
    detectLanguage(
      typeof navigator !== "undefined" ? navigator.languages : undefined,
    )
  );
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<Language>(
    resolveInitialLanguage,
  );

  // setLanguage setzt den State und schreibt immer in den Sprach_Cache
  // (Req 4.2, 7.5). Kein Backend-Aufruf an dieser Stelle.
  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang);
    writeLanguage(lang);
  }, []);

  // Spiegelt die Aktive_Sprache auf das <html lang>-Attribut, damit assistive
  // Technologien die Dokumentsprache korrekt interpretieren (Barrierearmut).
  // Guard für Umgebungen ohne `document` (z. B. SSR/Tests).
  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.lang = language;
    }
  }, [language]);

  // t/formatDate/formatTime sind an die Aktive_Sprache gebunden und auf
  // `language` memoisiert. Bei Sprachwechsel entstehen neue Funktions-
  // Identitäten, sodass Texte und Datums-/Zeitangaben ohne Reload neu
  // aufgelöst/formatiert werden (Req 4.3, 9.2).
  const value = useMemo<I18nContextValue>(() => {
    return {
      language,
      setLanguage,
      t: (key: string, params?: TranslateParams) =>
        translate(catalog, language, key, params),
      formatDate: (iso: string, opts?: Intl.DateTimeFormatOptions) =>
        baseFormatDate(language, iso, opts),
      formatTime: (iso: string, opts?: Intl.DateTimeFormatOptions) =>
        baseFormatTime(language, iso, opts),
    };
  }, [language, setLanguage]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (ctx === null) {
    throw new Error("useI18n must be used inside <I18nProvider>");
  }
  return ctx;
}
